import {
  computed,
  EventEmitter,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, debounceTime, filter, map, of, Subject, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
// import { ChatSessionDTO } from '../dtos/chat-session.dto';
import { UserStore } from '../app-state/user.store';
import { ChatStore } from '../app-state/chat.store';
import {
  SessionStore,
  mapSourceToAiScribeJob,
} from '../app-state/session.store';
import {
  WebsocketPopupService,
  WebsocketPopupType,
} from './websocket-popup.service';
import {
  AiScribeJob,
  SourceAiScribeJob,
  SUB_REPORT_STATUS,
} from '../interfaces/aiScribeJob.interface';
import {
  getStandaloneFormVariant,
  getSubReportFailureReason,
  SIBLING_REPORT_CONFIG,
} from '../utils/ptp-report.constants';
import {
  GeneratedDocumentChangedEvent,
  isGeneratedDocumentType,
} from '../interfaces/generated-document.interface';
import { EhrPushStatusEvent } from '../interfaces/ehr-push.interface';
import {
  ReportRunChangedEvent,
  ReportStartStoppedEvent,
} from '../interfaces/report.interface';
import Pusher from 'pusher-js';

/**
 * Generated-document types whose BASELINE_GENERATION_FAILED is shown in its own
 * tab rather than as a toast.
 *
 * Every SIBLING form: each is a best-effort side-artifact of the PR report, so
 * a failure should not interrupt the user, and with three of them per run
 * toasting each one would be three interruptions for a single report. The PR
 * report's own failure still toasts.
 */
const SILENT_FAILURE_DOCUMENT_TYPES: ReadonlySet<string> = new Set([
  'RFA',
  'INJECTION',
  'WORK_STATUS',
]);

export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error',
}

export interface MmiInputsChangedEvent {
  session_id: string;
  requestedAction: 'qme' | 'impairment';
  mmiOutputId: string;
  changedEvidenceCategories: string[];
  /** Set when the check was reserved for one report's start. */
  reportId?: string;
}

/**
 * Operational notification for a technical MMI-gate failure after its retry
 * policy is exhausted. This intentionally carries no clinical MMI status.
 */
export interface MmiGateFailedOpenEvent {
  sessionId: string;
  requestedAction: 'qme' | 'impairment';
  warningMessage?: string;
  /** Set when the check was reserved for one report's start. */
  reportId?: string;
}

/** An MMI result from a check reserved for one report's start. */
export interface ReportMmiStatusEvent {
  sessionId: string;
  reportId: string;
  mmiOutputId: string;
  status: string | null;
  /** Whether the report's start continued into a run. */
  reportStarted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  // TODO: Handle actions summary, icd and title

  // Add connection state signal
  private readonly _connectionState = signal<ConnectionState>(
    ConnectionState.DISCONNECTED,
  );
  readonly connectionState = this._connectionState.asReadonly();

  connection?: WebSocket;
  private pusher: Pusher | null = null;
  private connectionId?: string;
  private readonly messageEmitter = new EventEmitter<any>();
  private pingInterval: any;
  private heartbeatInterval: any;
  private socketId?: any | null;
  private readonly userStore = inject(UserStore);
  private readonly chatStore = inject(ChatStore);
  private readonly sessionStore = inject(SessionStore);

  private readonly websocketPopup = inject(WebsocketPopupService);

  // Subjects for tab notification dots - emit session_id
  summaryUpdateEmitter = new Subject<string>();
  quickReferenceUpdateEmitter = new Subject<string>();
  impairmentRatingUpdateEmitter = new Subject<string>();
  qmeReportUpdateEmitter = new Subject<string>();
  medicalLegalReportUpdateEmitter = new Subject<string>();
  piReportUpdateEmitter = new Subject<string>();
  ptpReportUpdateEmitter = new Subject<string>();
  sibtfReportUpdateEmitter = new Subject<string>();
  physicalExamUpdateEmitter = new Subject<string>();
  aiScribeUpdateEmitter = new Subject<string>();
  reviewOfRecordsUpdateEmitter = new Subject<string>();
  previsitQuestionnaireUpdateEmitter = new Subject<string>();
  // Terminal audio (DSS→MP3) conversion signal, so the media library can flip a
  // DSS row's "converting" indicator to "failed" (or refresh on success) live.
  recordingConversionEmitter = new Subject<{
    sessionId: string;
    aiScribeId: string;
    status: 'converted' | 'failed';
  }>();
  generatedDocumentChangedEmitter =
    new Subject<GeneratedDocumentChangedEvent>();
  /** A report's run changed status or stage. */
  reportRunChangedEmitter = new Subject<ReportRunChangedEvent>();
  /**
   * Completion-only signal for an on-demand EHR push (SUCCEEDED/FAILED never
   * PENDING — the trigger response itself carries that). Consumers must match
   * session_id + generatedDocumentId + generatedDocumentVersionId against
   * whatever version they currently have loaded before applying it, since a
   * push can complete for a version the consumer has since moved on from.
   */
  ehrPushStatusEmitter = new Subject<EhrPushStatusEvent>();
  transcribeEndEmitter = new Subject<{ sessionId: string; jobId: string }>();
  // An AI Scribe was deleted. The bind handler drops the store row; this tells
  // components holding their own copy (viewer, playback dock) to let go too.
  aiScribeDeletedEmitter = new Subject<{
    sessionId: string;
    aiScribeId: string;
    jobName: string | null;
    deletedBy: string | null;
  }>();

  // Voice-driven report edit events
  reportEditStartedEmitter = new Subject<{
    sessionId: string;
    outputType: string;
    instruction: string;
  }>();
  reportEditCompletedEmitter = new Subject<{
    sessionId: string;
    outputType: string;
    newOutputId: string | null;
    editedFields: string[];
  }>();
  reportEditFailedEmitter = new Subject<{
    sessionId: string;
    outputType: string;
  }>();

  // Voice-driven AI-SCRIBE edit events (PR-1 / PR-2 / PR-4 / RFA).
  //
  // Deliberately SEPARATE Subjects fed by separate Pusher event names
  // (scribe_edit_*), not the QME report_edit_* ones. Both the QME report panel
  // and the scribe panel are mounted at the same time, so sharing the QME
  // emitters would mean a PR-1 edit fires QME's subscribers too: a second
  // "Report updated" toast, and — worse — QME's _localVoiceEditing signal
  // cleared, prematurely dismissing the "AI is editing your report..." banner
  // of a genuinely in-flight QME edit. Separate names make that impossible by
  // construction rather than by an outputType guard in every subscriber.
  //
  // Costs nothing server-side: the websocket broker's default branch forwards
  // any unrecognised event type through as-is (eventNameToSend = type), which
  // is already how report_edit_* travels.
  scribeEditStartedEmitter = new Subject<{
    sessionId: string;
    aiScribeId: string;
    outputType: string;
    instruction: string;
  }>();
  scribeEditCompletedEmitter = new Subject<{
    sessionId: string;
    aiScribeId: string;
    outputType: string;
    newContentId: string | null;
    newVersion: number | null;
    editedFields: string[];
  }>();
  scribeEditFailedEmitter = new Subject<{
    sessionId: string;
    aiScribeId: string;
    outputType: string;
  }>();

  notifTaskUpdateEmitter = new Subject<{
    session_id: string;
    task_id: string;
  }>();
  /**
   * Lightweight notification when the backend updates a session's
   * report-generation status (RUNNING / COMPLETED / FAILED). Carries no PHI.
   * Subscribers should refresh `activeSession` so reactive UI (e.g. the red
   * Regenerate button on FAILED, or the resumed spinner on RUNNING) updates
   * without having to wait for the next manual session re-fetch.
   */
  reportGenerationStatusEmitter = new Subject<{
    session_id: string;
    output_type: string;
    /**
     * Server-driven status. We accept arbitrary strings here so the frontend
     * doesn't crash if the backend introduces a new state — consumers should
     * compare with case-insensitivity and treat unknown values as no-op.
     */
    status: string;
  }>();
  /**
   * Fired when the backend's post-persist MMI evidence-drift check finds
   * evidence changed since an MMI check started — the requested QME or
   * standalone Impairment report never actually started. Subscribers must
   * clear any MMI-checking/pending-force-decision state without assuming the
   * report began, and prompt the user to regenerate.
   */
  mmiInputsChangedEmitter = new Subject<MmiInputsChangedEvent>();
  /**
   * The MMI gate could not complete technically, so the requested report was
   * handed off without creating an `mmi_status` clinical output.
   */
  mmiGateFailedOpenEmitter = new Subject<MmiGateFailedOpenEvent>();
  /** The `mmi_status` of a check reserved for one report's start. */
  reportMmiStatusEmitter = new Subject<ReportMmiStatusEvent>();
  /** A report start that waited at its gate did not continue. */
  reportStartStoppedEmitter = new Subject<ReportStartStoppedEvent>();
  private readonly token = this.userStore.token;
  private readonly heartbeatEndpoint = `${environment.api_host}wss/heartbeat`;
  private readonly connectedEndpoint = `${environment.api_host}wss/connected`;
  private readonly disconnectEndpoint = `${environment.api_host}wss/disconnected`;

  private readonly _connectionId = signal<string | undefined>(undefined);
  http: any;

  get connectionId$() {
    return computed(this._connectionId);
  }

  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;

  // Reconnect/input-drift reconciliation: the first Pusher 'connected' event
  // is the initial connect (session open already fetches full state, so no
  // extra refresh is needed); every subsequent 'connected' is a genuine
  // reconnect that may have missed live events while disconnected. A burst of
  // reconnects (native Pusher retry + the visibility-reconnect path can both
  // fire close together) is coalesced into a single refresh via debounce.
  private hasConnectedPusherOnce = false;
  private readonly reconnectReconciliationDebounceMs = 500;
  private readonly reconnectReconciliation$ = new Subject<void>();
  private reconnectTimeout?: any;
  private readonly reconnectInterval = 3000; // 3 seconds
  private visibilityChangeHandler?: () => void;
  private visibilityReconnectAttempts = 0;
  private visibilityReconnectTimeout?: any;
  private pusherReconnectAttempts = 0;
  private pusherReconnectTimeout?: any;
  public viewedAiScribeIdsPerSession = new Map<string, Set<string>>();

  /**
   * @param markUnviewed clear this job's "viewed" marker so the scribe list
   *   shows its processed highlight again. TRUE only for user-facing events
   *   ('scribe') that mean new content the user has not seen. The silent
   *   refresh events ('scribe_refresh', 'sub_report_status') must NOT clear
   *   it: they fire on every run — a sibling form's gate decision fires even
   *   when nothing was produced, and there are three such forms — and
   *   re-flagging an already-read scribe as new on each one is noise the user
   *   cannot clear except by opening it again.
   */
  private refreshAiScribeJobFromEvent(
    sessionId?: string,
    jobId?: string,
    onComplete?: () => void,
    markUnviewed = true,
    // Receives the freshly fetched job, whichever session is on screen.
    onJob?: (job: AiScribeJob) => void,
  ): void {
    if (!sessionId) {
      return;
    }

    this.sessionStore
      .fetchActiveSessionAiScribeJobs(
        sessionId,
        'AiScribeContents,FileAsset,ai_scribe_logs,ai_scribe_retry_logs',
      )
      ?.pipe(
        take(1),
        catchError((error) => {
          console.error(
            '[WebSocket] Failed to refresh AI Scribe job from event',
            {
              sessionId,
              jobId,
              error,
            },
          );
          return of([] as SourceAiScribeJob[]);
        }),
      )
      .subscribe((aiScribeJobsSource: SourceAiScribeJob[]) => {
        if (!aiScribeJobsSource?.length) {
          return;
        }

        const targetJob = jobId
          ? aiScribeJobsSource.find((job) => job.aiScribeId === jobId)
          : aiScribeJobsSource[0];

        if (!targetJob) {
          return;
        }
        if (markUnviewed) {
          const viewedIds = this.viewedAiScribeIdsPerSession.get(sessionId);
          if (viewedIds && jobId) {
            viewedIds.delete(jobId);
          }
        }

        const aiScribeJob: AiScribeJob = mapSourceToAiScribeJob(targetJob);
        onJob?.(aiScribeJob);

        const currentActiveSession = this.sessionStore.activeSession();

        if (currentActiveSession?.id === sessionId) {
          this.sessionStore
            .setActiveSession({ id: sessionId } as any)
            .subscribe({
              next: () => {
                this.sessionStore.updateJobMetadata(
                  sessionId,
                  aiScribeJob.aiScribeId,
                  aiScribeJob,
                );
                this.aiScribeUpdateEmitter.next(sessionId);
                onComplete?.();
              },
              error: (error) => {
                console.error(
                  '[WebSocket] Failed to activate session for AI Scribe event',
                  {
                    sessionId,
                    jobId,
                    error,
                  },
                );
                this.sessionStore.updateJobMetadata(
                  sessionId,
                  aiScribeJob.aiScribeId,
                  aiScribeJob,
                );
                this.aiScribeUpdateEmitter.next(sessionId);
                onComplete?.();
              },
            });
          return;
        }

        this.sessionStore.updateJobMetadata(
          sessionId,
          aiScribeJob.aiScribeId,
          aiScribeJob,
        );
        this.aiScribeUpdateEmitter.next(sessionId);
        onComplete?.();
      });
  }

  /**
   * Toast a standalone-form job's FAILED / NOT_REQUIRED outcome. Only for the
   * job's OWN form — a PR job's sibling forms stay silent, as they always have.
   * Reads the reason off the freshly fetched job (its sub-report error_code).
   */
  private notifyStandaloneFormOutcome(
    job: AiScribeJob,
    data: any,
    sessionId?: string,
  ): void {
    const variant = getStandaloneFormVariant(job.formType);
    if (!variant) return;
    const config = SIBLING_REPORT_CONFIG[variant];
    const payload = data?.data ?? data;
    const outputType = payload?.output_type;
    if (outputType && outputType !== config.outputType) return;
    const status =
      payload?.sub_report_status ?? job.subReportStatuses?.[config.outputType];

    if (status === SUB_REPORT_STATUS.FAILED) {
      const reason = getSubReportFailureReason(job, config.outputType);
      this.notifyWithCase(
        'error',
        `${config.reportLabel} Generation Failed`,
        reason
          ? `${reason} Add it as a note/dictation to try again.`
          : 'Report generation failed. Try again, and if it keeps failing, ' +
              'contact support.',
        `standalone_form_failed-${job.aiScribeId}`,
        sessionId,
        undefined,
      );
    } else if (
      status === SUB_REPORT_STATUS.NOT_REQUIRED &&
      config.notRequiredPanel
    ) {
      this.notifyWithCase(
        'info',
        config.notRequiredPanel.title,
        config.notRequiredPanel.message,
        `standalone_form_not_required-${job.aiScribeId}`,
        sessionId,
        undefined,
      );
    }
  }

  private getEventSessionId(data: any): string | undefined {
    return (
      data?.data?.session_id ||
      data?.data?.sessionId ||
      data?.session_id ||
      data?.sessionId
    );
  }

  private getEventJobId(data: any): string | undefined {
    return (
      data?.data?.jobId || data?.data?.job_id || data?.jobId || data?.job_id
    );
  }

  private getEventName(data: any): string | undefined {
    return data?.data?.name || data?.name;
  }

  /**
   * "Generated" toast + unread-dot emitter for a report that finished on
   * Temporal (only a `report_generation_status` COMPLETED arrives; the legacy
   * rabbitmq flow also sent a per-user content event such as `summary`).
   * Uses the SAME notification key as the legacy handler, so a legacy run that
   * sends both events still shows a single toast.
   */
  private notifyReportCompletedFromStatus(
    sessionId: string,
    outputType: string,
  ): void {
    if (outputType !== 'summary') return;
    try {
      this.notifyWithCase(
        'info',
        'Summary generated',
        'A new summary has been generated.',
        `summary-${sessionId}`,
        sessionId,
      );
    } catch {
      // Error showing notification handled silently
    }
    this.summaryUpdateEmitter.next(sessionId);
  }

  /**
   * Raise a websocket-driven pop-up. Every websocket pop-up goes through here
   * so the notification pop-up scope is applied in one place.
   */
  private notifyWithCase(
    type: WebsocketPopupType,
    title: string,
    message: string,
    key?: string,
    sessionId?: string,
    sessionName?: string,
    showSessionName: boolean = false,
  ): void {
    this.websocketPopup.notify({
      type,
      title,
      message,
      key,
      sessionId,
      sessionName,
      showSessionName,
    });
  }

  private notifyAiScribeEvent(
    type: 'info' | 'warning' | 'error' | 'success',
    title: string,
    message: string,
    keyPrefix: string,
    jobId?: string,
    sessionId?: string,
    sessionName?: string,
  ): void {
    this.notifyWithCase(
      type,
      title,
      message,
      `${keyPrefix}-${jobId ?? sessionId ?? 'unknown'}-${Date.now()}`,
      sessionId,
      sessionName,
      true,
    );
  }

  constructor(private readonly httpService: HttpClient) {
    this.setupVisibilityChangeHandler();
    this.setupReconnectReconciliation();
  }

  private setupReconnectReconciliation(): void {
    this.reconnectReconciliation$
      .pipe(debounceTime(this.reconnectReconciliationDebounceMs))
      .subscribe(() => this.reconcileActiveSessionAfterReconnect());
  }

  // Called from the Pusher 'connected' binding on every successful (re)connect.
  private handlePusherConnected(): void {
    if (!this.hasConnectedPusherOnce) {
      this.hasConnectedPusherOnce = true;
      return;
    }
    this.reconnectReconciliation$.next();
  }

  /**
   * Development-only proof that a Pusher MMI event reached this browser.
   * Deliberately accepts pre-sanitized metadata rather than the event payload,
   * which can include clinical evidence and must never be written to the
   * browser console.
   */
  private logMmiWebsocketReceipt(
    eventName: string,
    details: Record<string, string | boolean | undefined>,
  ): void {
    if (environment.production) {
      return;
    }

    console.log('[DEBUG-mmi-ws-20260806] received', {
      eventName,
      ...details,
    });
  }

  private reconcileActiveSessionAfterReconnect(): void {
    const activeSession = this.sessionStore.activeSession();
    if (!activeSession) return;
    this.sessionStore
      .setActiveSession({ id: activeSession.id } as any)
      .subscribe({
        error: (error) =>
          console.error('[WS] Reconnect reconciliation failed', error),
      });
  }

  private setupVisibilityChangeHandler() {
    // Handle page visibility changes (e.g., after auto-lock)
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible again - check connection status
        const currentState = this.connectionState();
        const pusherState = this.pusher?.connection.state;

        // Reconnect if disconnected or if Pusher is not connected
        if (
          currentState === ConnectionState.DISCONNECTED ||
          currentState === ConnectionState.ERROR ||
          pusherState === 'disconnected' ||
          pusherState === 'failed' ||
          !this.pusher
        ) {
          this.handleVisibilityReconnect();
        } else if (
          pusherState === 'connecting' ||
          currentState === ConnectionState.CONNECTING
        ) {
          // Already connecting, do nothing
        } else {
          // Check if connection is actually working by verifying Pusher state
          if (pusherState !== 'connected') {
            this.handleVisibilityReconnect();
          }
        }
      }
    };

    // Add event listener
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
    }
  }

  private handleVisibilityReconnect() {
    // Clear any existing visibility reconnect timeout
    if (this.visibilityReconnectTimeout) {
      clearTimeout(this.visibilityReconnectTimeout);
    }

    if (this.visibilityReconnectAttempts < this.maxReconnectAttempts) {
      this.visibilityReconnectAttempts++;

      // Reset main reconnect attempts to allow fresh connection
      this.reconnectAttempts = 0;

      this.visibilityReconnectTimeout = setTimeout(() => {
        this.connect(true);

        // Check if connection succeeded after a delay
        setTimeout(() => {
          const currentState = this.connectionState();
          const pusherState = this.pusher?.connection.state;

          if (
            currentState === ConnectionState.CONNECTED &&
            pusherState === 'connected'
          ) {
            // Connection successful, reset attempts
            this.visibilityReconnectAttempts = 0;
          } else {
            // Connection failed, retry
            this.handleVisibilityReconnect();
          }
        }, 2000); // Wait 2 seconds to check connection status
      }, this.reconnectInterval);
    } else {
      this.visibilityReconnectAttempts = 0; // Reset for next visibility change
    }
  }

  connect(pusher_retry: boolean = true) {
    if (this.connectionState() === ConnectionState.CONNECTING) {
      return;
    }

    // Clear any existing connections
    if (this.connection) {
      this.connection.close();
      this.connection = undefined;
    }
    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }

    this._connectionState.set(ConnectionState.CONNECTING);

    // Connect to Pusher/Soketi for websocket events
    if (pusher_retry) {
      this.connectToPusher();
    }

    // Keeping existing WebSocket connection for chat/other features
    try {
      this.connection = new WebSocket(
        `${environment.chat_url}?token=${this.token()}`,
      );
      this.connection.onerror = (error) => {
        this._connectionState.set(ConnectionState.ERROR);
        this.handleReconnect();
      };

      this.connection.onclose = () => {
        this._connectionState.set(ConnectionState.DISCONNECTED);
        this.handleReconnect();
      };

      this.connection.onopen = () => {
        this._connectionState.set(ConnectionState.CONNECTED);
        this.startPing();

        this.connection!.onmessage = (event) => {
          const message = JSON.parse(event.data);
          switch (message.action) {
            case 'connection_id':
              this.connectionId = message.data;
              this.sendPing_id();
              this._connectionId.set(message.data);
              break;
            case 'ocr_complete':
              // console.log('OCR Complete:', message.data);
              const ocr_session_id = message.data.session_id;
              const document_id = message.data.document_id;
              const ocr_output = message.data.ocr_output;
              this.sessionStore.updateDocumentOcrOutput(
                ocr_session_id,
                document_id,
                ocr_output,
              );
              // A newly OCR'd document changes the input set every report's
              // freshness is measured against, and the server is the only
              // authority on that comparison. Debounced inside the store, so a
              // multi-document upload costs one request, not one per document.
              this.sessionStore.refreshReportFreshness(ocr_session_id);
              break;
            case 'message':
              this.chatStore.receiveMessage(message.data);
              break;
            case 'stream_message':
              this.chatStore.addOrUpdateStreamingMessage(
                message.data.chat_session_id,
                message.data.token,
              );
              break;

            case 'stream_end':
              this.chatStore.finalizeStreamingMessage(
                message.data.chat_session_id,
              );
              break;
            case 'icd':
              // console.log('ICD:', message.data);
              break;
            case 'ping':
              // console.log('Pong received');
              break;
            case 'scribe':
              // console.log(message);
              this.refreshActiveAiScribeJob(message.data.sessionId);
              break;
            case 'scribe_refresh':
              // Same refresh as 'scribe', no toast — see the Pusher binding.
              this.refreshActiveAiScribeJob(message.data?.sessionId);
              break;
            case 'sub_report_status':
            case 'rfa_status':
              // A sibling form's gate decided (GENERATING / NOT_REQUIRED), or
              // its run completed or failed. Refresh only — deliberately no
              // toast. A declined form is not news to the user, the gate runs
              // on every PR-1/PR-2 run, and there are now three forms doing
              // this per run. Completion and failure notices ride
              // generated_document_changed instead.
              //
              // 'rfa_status' is the pre-generalisation name, kept for one
              // release so an in-flight message from the previous build still
              // refreshes the tab.
              this.refreshActiveAiScribeJob(message.data?.sessionId);
              break;
            case 'recording_converted':
              // A non-playable upload (e.g. DSS) was converted to MP3 and
              // recordingFileId now points at it. Refresh the job list so the
              // media library picks up the playable recording, but do NOT set the
              // active job (would confuse the user mid-view; playback isn't
              // offered until the scribe fully completes).
              this.refreshActiveAiScribeJob(message.data?.sessionId, false);
              this.recordingConversionEmitter.next({
                sessionId: message.data?.sessionId,
                aiScribeId: message.data?.aiScribeId,
                status: 'converted',
              });
              break;
            case 'recording_conversion_failed':
              // Best-effort playback sugar: the original upload stays in place
              // (unplayable in-browser). Signal so the DSS row flips to "failed".
              console.warn(
                'Audio conversion failed for AI scribe',
                message.data?.aiScribeId,
              );
              this.recordingConversionEmitter.next({
                sessionId: message.data?.sessionId,
                aiScribeId: message.data?.aiScribeId,
                status: 'failed',
              });
              break;
            case 'transcribe_end':
              // Handle transcription completion event
              if (message.data?.sessionId && message.data?.jobId) {
                this.transcribeEndEmitter.next({
                  sessionId: message.data.sessionId,
                  jobId: message.data.jobId,
                });
              }
              break;

            case 'report_edit_completed': {
              const editSessionId = message.data?.sessionId;
              const editedFields: string[] = message.data?.editedFields ?? [];

              this.reportEditCompletedEmitter.next({
                sessionId: editSessionId,
                outputType: message.data?.outputType,
                newOutputId: message.data?.newOutputId ?? null,
                editedFields,
              });

              // Global notification — shown even if the voice panel is closed
              try {
                const fieldSummary = editedFields.length
                  ? `${editedFields.length} section${editedFields.length > 1 ? 's' : ''} updated.`
                  : 'No sections changed.';
                this.notifyWithCase(
                  'success',
                  'Report updated',
                  `Voice edit applied — ${fieldSummary}`,
                  `report_edit_completed-${editSessionId}-${Date.now()}`,
                  editSessionId,
                  undefined,
                );
              } catch {
                /* silent */
              }

              // Re-hydrate QME report so view refreshes automatically
              if (editSessionId) {
                this.qmeReportUpdateEmitter.next(editSessionId);
              }
              break;
            }

            case 'report_edit_failed':
              this.reportEditFailedEmitter.next({
                sessionId: message.data?.sessionId,
                outputType: message.data?.outputType,
              });
              // Global error notification
              try {
                this.notifyWithCase(
                  'error',
                  'Voice edit failed',
                  'The voice edit could not be applied. Please try again.',
                  `report_edit_failed-${message.data?.sessionId}-${Date.now()}`,
                  message.data?.sessionId,
                  undefined,
                );
              } catch {
                /* silent */
              }
              break;

            case 'report_edit_started':
              this.reportEditStartedEmitter.next({
                sessionId: message.data?.sessionId,
                outputType: message.data?.outputType,
                instruction: message.data?.instruction,
              });
              break;

            // ── AI-scribe voice edit (PR-1 / PR-2 / PR-4 / RFA) ───────────
            // Raw-socket mirror of the Pusher binds. Until now these types hit
            // `default: break` and were silently discarded.
            case 'scribe_edit_started':
              this.scribeEditStartedEmitter.next({
                sessionId: message.data?.sessionId,
                aiScribeId: message.data?.aiScribeId,
                outputType: message.data?.outputType,
                instruction: message.data?.instruction,
              });
              break;

            case 'scribe_edit_completed': {
              const editSessionId = message.data?.sessionId;
              const aiScribeId = message.data?.aiScribeId;
              const editedFields: string[] = message.data?.editedFields ?? [];

              this.scribeEditCompletedEmitter.next({
                sessionId: editSessionId,
                aiScribeId,
                outputType: message.data?.outputType,
                newContentId: message.data?.newOutputId ?? null,
                newVersion: message.data?.newVersion ?? null,
                editedFields,
              });

              if (editSessionId) {
                this.refreshAiScribeJobFromEvent(
                  editSessionId,
                  aiScribeId,
                  () => {
                    try {
                      const fieldSummary = editedFields.length
                        ? `${editedFields.length} section${editedFields.length > 1 ? 's' : ''} updated.`
                        : 'No sections changed.';
                      this.notifyWithCase(
                        'success',
                        'Report updated',
                        `Voice edit applied — ${fieldSummary}`,
                        `scribe_edit_completed-${editSessionId}-${Date.now()}`,
                        editSessionId,
                        undefined,
                      );
                    } catch {
                      /* silent */
                    }
                  },
                  false,
                );
              }
              break;
            }

            case 'scribe_edit_failed':
              this.scribeEditFailedEmitter.next({
                sessionId: message.data?.sessionId,
                aiScribeId: message.data?.aiScribeId,
                outputType: message.data?.outputType,
              });
              try {
                this.notifyWithCase(
                  'error',
                  'Voice edit failed',
                  'The voice edit could not be applied. Please try again.',
                  `scribe_edit_failed-${message.data?.sessionId}-${Date.now()}`,
                  message.data?.sessionId,
                  undefined,
                );
              } catch {
                /* silent */
              }
              break;

            default:
              break;
          }
          this.messageEmitter.emit(message);
        };

        this.send('get_connection_id');
      };
    } catch (error) {
      this._connectionState.set(ConnectionState.ERROR);
      this.handleReconnect();
      throw error;
    }
  }

  private connectToPusher() {
    const token = this.token();
    const organizationUser = this.userStore.organizationUser();
    const organization = this.userStore.organization();

    if (!token || !organizationUser || !organization) {
      console.warn('[WS] Skipping Pusher connect: missing token/user/org', {
        hasToken: !!token,
        hasOrganizationUser: !!organizationUser,
        hasOrganization: !!organization,
      });
      return;
    }

    const userUuid = organizationUser.id;
    const orgUuid = organization.id;

    // Initialize Pusher
    this.pusher = new Pusher(environment.soketi_app_key, {
      cluster: 'mt1',
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${environment.api_host}wss/pusher/auth`,
      forceTLS: true,
      wsHost: environment.soketi_host,
      wsPort: Number(environment.soketi_port),
      auth: {
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    });

    this.pusher.connection.bind('connected', () => {
      const socketId = this.pusher?.connection.socket_id;
      this.socketId = socketId;

      // Reset reconnect attempts on successful connection
      this.pusherReconnectAttempts = 0;

      if (!socketId) return;

      this.handlePusherConnected();

      // Subscribe to the private user channel
      const userChannelName = `private-user-${orgUuid}-${userUuid}`;
      const userChannel = this.pusher?.subscribe(userChannelName);

      userChannel?.bind('pusher:subscription_error', (status: any) => {
        // Subscription error handled silently
        console.error('[WS] Pusher connection error', status);
      });

      userChannel?.bind('pusher:subscription_succeeded', () => {
        // Register connection with backend
        this.httpService
          .post(this.connectedEndpoint, {
            event: {
              channel: {
                connectionId: socketId,
              },
            },
            organization_uuid: orgUuid,
            user_uuid: userUuid,
          })
          .subscribe({
            next: () => {
              this.startHeartbeat(socketId);
            },
            error: (error) => {
              // Error registering connection handled silently
              console.error(
                '[WS] Failed to register connection with backend',
                error,
              );
            },
          });
      });

      // Listen for websocket events
      userChannel?.bind('summary', (data: any) => {
        const nowIso = new Date().toISOString();
        const session_id = data.data?.session_id || data.session_id;
        const session_name = data.data?.name || data.name;

        const applyFallback = () => {
          const newSummary = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'summary',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionSummary(session_id, newSummary);

          try {
            const notifKey = session_id
              ? `summary-${session_id}`
              : `summary-${newSummary.id}`;
            this.notifyWithCase(
              'info',
              'Summary generated',
              'A new summary has been generated.',
              notifKey,
              session_id,
              session_name,
            );
          } catch (err) {
            // Error showing notification handled silently
          }

          this.summaryUpdateEmitter.next(session_id);
        };

        if (!session_id) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === session_id) {
            this.sessionStore
              .setActiveSession({ id: session_id } as any)
              .subscribe({
                next: () => {
                  const nameFromStore = session_name;
                  try {
                    const notifKey = session_id
                      ? `summary-${session_id}`
                      : `summary-${session_id}`;
                    this.notifyWithCase(
                      'info',
                      'Summary generated',
                      'A new summary has been generated.',
                      notifKey,
                      session_id,
                      session_name,
                    );
                  } catch (err) {
                    // Error showing notification handled silently
                  }
                  if (session_id) {
                    this.summaryUpdateEmitter.next(session_id);
                  }
                },
                error: () => {
                  applyFallback();
                },
              });
          } else {
            try {
              const notifKey = session_id
                ? `summary-${session_id}`
                : `summary-${session_id}`;
              this.notifyWithCase(
                'info',
                'Summary generated',
                'A new summary has been generated.',
                notifKey,
                session_id,
                session_name,
              );
            } catch (err) {
              // Error showing notification handled silently
            }
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('quick_reference', (data: any) => {
        const nowIso = new Date().toISOString();
        const quick_ref_session_id = data.data?.session_id || data.session_id;

        const applyFallback = () => {
          const newQuickReference = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'quick_reference',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionQuickReference(
            quick_ref_session_id,
            newQuickReference,
          );
          this.quickReferenceUpdateEmitter.next(quick_ref_session_id);
        };

        if (!quick_ref_session_id) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === quick_ref_session_id) {
            this.sessionStore
              .setActiveSession({ id: quick_ref_session_id } as any)
              .subscribe({
                next: () => {
                  this.quickReferenceUpdateEmitter.next(quick_ref_session_id);
                },
                error: () => {
                  applyFallback();
                },
              });
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('impairment_rating', (data: any) => {
        const nowIso = new Date().toISOString();
        const impairmentSessionId = data.data?.session_id || data.session_id;

        const applyFallback = () => {
          const newImpairmentRating = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'impairment_rating',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionImpairmentRating(
            impairmentSessionId,
            newImpairmentRating,
          );
          if (impairmentSessionId) {
            this.impairmentRatingUpdateEmitter.next(impairmentSessionId);
          }
        };

        if (!impairmentSessionId) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === impairmentSessionId) {
            this.sessionStore
              .setActiveSession({ id: impairmentSessionId } as any)
              .subscribe({
                next: () => {
                  this.impairmentRatingUpdateEmitter.next(impairmentSessionId);
                },
                error: () => {
                  applyFallback();
                },
              });
          }
        } catch (err) {
          applyFallback();
        }
      });

      const bindMmiStatusEvent = (
        eventName: string,
        defaultOutputType: string,
      ) => {
        userChannel?.bind(eventName, (data: any) => {
          const nowIso = new Date().toISOString();
          const sessionId = data.data?.session_id || data.session_id;
          this.logMmiWebsocketReceipt(eventName, {
            sessionId: typeof sessionId === 'string' ? sessionId : undefined,
            outputId:
              typeof data.data?.id === 'string' ? data.data.id : undefined,
            outputType:
              typeof data.data?.output_type === 'string'
                ? data.data.output_type
                : defaultOutputType,
          });

          const applyFallback = () => {
            const newMmiStatus = {
              id: crypto.randomUUID(),
              value: data.data?.value || data.value,
              output_type: data.data?.output_type || defaultOutputType,
              task_id: data.data?.task_id,
              createdAt: data.data?.createdAt || nowIso,
              updatedAt: data.data?.updatedAt || nowIso,
            };
            this.sessionStore.updateActiveSessionMmiStatus(
              sessionId,
              newMmiStatus,
            );
          };

          if (!sessionId) {
            applyFallback();
            return;
          }

          const reportId = readReportId(data.data);
          if (reportId && typeof data.data?.id === 'string') {
            const status = data.data?.value?.mmi_status?.status;
            this.reportMmiStatusEmitter.next({
              sessionId,
              reportId,
              mmiOutputId: data.data.id,
              status: typeof status === 'string' ? status : null,
              reportStarted:
                (data.data?.reportStarted ?? data.data?.report_started) === true,
            });
          }

          try {
            const currentActiveId = this.sessionStore.activeSession()?.id;
            if (currentActiveId === sessionId) {
              this.sessionStore
                .setActiveSession({ id: sessionId } as any)
                .subscribe({
                  next: () => {},
                  error: () => {
                    applyFallback();
                  },
                });
            } else {
              applyFallback();
            }
          } catch {
            applyFallback();
          }
        });
      };
      // The backend only ever publishes the 'mmi_status' event; this binding
      // is a defensive, currently-unreachable listener for an event name the
      // backend has never sent.
      bindMmiStatusEvent('mmi_status_impairment', 'mmi_status_impairment');
      bindMmiStatusEvent('mmi_status', 'mmi_status');

      // Emitted instead of (in addition to no further mmi_status push) when
      // the backend's post-persist evidence-drift check stops a QME/
      // Impairment request. next() runs synchronously here, before the
      // active-session refresh below resolves — subscribers rely on that
      // ordering to suppress a stale mmi_status-driven prompt for the same
      // now-drifted output.
      userChannel?.bind('mmi_inputs_changed', (data: any) => {
        const event = this.parseMmiInputsChangedEvent(data);
        const payload = data?.data ?? data;
        this.logMmiWebsocketReceipt('mmi_inputs_changed', {
          sessionId:
            typeof payload?.session_id === 'string'
              ? payload.session_id
              : undefined,
          mmiOutputId:
            typeof payload?.mmi_output_id === 'string'
              ? payload.mmi_output_id
              : undefined,
          requestedAction:
            typeof payload?.requested_action === 'string'
              ? payload.requested_action
              : undefined,
          valid: Boolean(event),
        });
        if (!event) return;

        this.mmiInputsChangedEmitter.next(event);

        if (this.sessionStore.activeSession()?.id === event.session_id) {
          this.sessionStore
            .setActiveSession({ id: event.session_id } as any)
            .subscribe({ next: () => {}, error: () => {} });
        }
      });

      userChannel?.bind('mmi_gate_failed_open', (data: any) => {
        try {
          const event = this.parseMmiGateFailedOpenEvent(data);
          const payload = data?.data ?? data;
          this.logMmiWebsocketReceipt('mmi_gate_failed_open', {
            sessionId:
              typeof payload?.session_id === 'string'
                ? payload.session_id
                : undefined,
            requestedAction:
              typeof payload?.requestedAction === 'string'
                ? payload.requestedAction
                : undefined,
            valid: Boolean(event),
          });
          if (!event) return;
          this.mmiGateFailedOpenEmitter.next(event);
        } catch {
          // A malformed warning must not interrupt report lifecycle events.
        }
      });

      userChannel?.bind('qme_report', (data: any) => {
        const nowIso = new Date().toISOString();
        const qme_report_session_id = data.data?.session_id || data.session_id;
        const session_name = data.data?.name || data.name;

        const applyFallback = () => {
          const newQmeReport = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'qme_report',
            output_format: data.data?.output_format || data.output_format,
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionQmeReport(
            qme_report_session_id,
            newQmeReport,
          );

          try {
            const notifKey = qme_report_session_id
              ? `qme_report-${qme_report_session_id}`
              : `qme_report-${newQmeReport.id}`;
            this.notifyWithCase(
              'info',
              'QME Report generated',
              'A new QME report has been generated.',
              notifKey,
              qme_report_session_id,
              session_name,
            );
          } catch (err) {
            // Error showing notification handled silently
          }

          this.qmeReportUpdateEmitter.next(qme_report_session_id);
        };

        if (!qme_report_session_id) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === qme_report_session_id) {
            this.sessionStore
              .setActiveSession({ id: qme_report_session_id } as any)
              .subscribe({
                next: () => {
                  const nameFromStore = session_name;
                  try {
                    const notifKey = qme_report_session_id
                      ? `qme_report-${qme_report_session_id}`
                      : `qme_report-${crypto.randomUUID()}`;
                    this.notifyWithCase(
                      'info',
                      'QME Report generated',
                      'A new QME report has been generated.',
                      notifKey,
                      qme_report_session_id,
                      nameFromStore || session_name,
                    );
                  } catch (err) {
                    // Silent
                  }
                  this.qmeReportUpdateEmitter.next(qme_report_session_id);
                },
                error: () => {
                  applyFallback();
                },
              });
          } else {
            const nameFromStore = session_name;
            try {
              const notifKey = qme_report_session_id
                ? `qme_report-${qme_report_session_id}`
                : `qme_report-${crypto.randomUUID()}`;
              this.notifyWithCase(
                'info',
                'QME Report generated',
                'A new QME report has been generated.',
                notifKey,
                qme_report_session_id,
                nameFromStore || session_name,
              );
            } catch (err) {
              // Silent
            }
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('report_run_changed', (data: any) => {
        const event = this.parseReportRunChangedEvent(data);
        if (event) this.reportRunChangedEmitter.next(event);
      });

      userChannel?.bind('report_start_stopped', (data: any) => {
        const event = this.parseReportStartStoppedEvent(data);
        if (event) this.reportStartStoppedEmitter.next(event);
      });

      userChannel?.bind('generated_document_changed', (data: any) => {
        const event = this.parseGeneratedDocumentChangedEvent(data);
        if (!event) return;

        if (event.event_type === 'BASELINE_GENERATION_FAILED') {
          // SIBLING-form failures are shown IN their own tab ("<Form>
          // Generation Failed"), driven by the sub-report status — no toast.
          // The tab is the right place for it: a sibling is a best-effort
          // side-artifact of the PR report, so a failure is not something to
          // interrupt the user over, and it would arrive alongside the PR
          // report's own notices. With three sibling forms per run, toasting
          // each failure would be three interruptions for one report.
          if (SILENT_FAILURE_DOCUMENT_TYPES.has(event.generated_document_type)) {
            this.generatedDocumentChangedEmitter.next(event);
            return;
          }
          try {
            const title = this.getGeneratedDocumentFailureNotificationTitle(
              event.generated_document_type,
            );
            // Key collapses repeat failures with the same error_code into one
            // toast — retries of the same root cause shouldn't stack.
            this.notifyWithCase(
              'error',
              title,
              'Report generation failed. Try again from the report tab. ' +
                'If it keeps failing, contact support.',
              `generated_document_failed-${event.session_id}-${event.generated_document_id}-${event.error_code}`,
              event.session_id,
              undefined,
            );
          } catch {
            // Error showing notification handled silently.
          }
        } else {
          try {
            const title = this.getGeneratedDocumentNotificationTitle(
              event.generated_document_type,
            );
            this.notifyWithCase(
              'info',
              title,
              'A new report is ready.',
              `generated_document_changed-${event.session_id}-${event.generated_document_id}-${event.version_number}`,
              event.session_id,
              undefined,
            );
          } catch {
            // Error showing notification handled silently.
          }
        }

        this.generatedDocumentChangedEmitter.next(event);

        if (event.event_type !== 'BASELINE_GENERATION_FAILED') {
          if (event.generated_document_type === 'QME_REPORT') {
            this.qmeReportUpdateEmitter.next(event.session_id);
          } else if (event.generated_document_type === 'PI_REPORT') {
            this.piReportUpdateEmitter.next(event.session_id);
          }
        }
      });

      userChannel?.bind('sibtf_report', (data: any) => {
        const nowIso = new Date().toISOString();
        const sessionId = data.data?.session_id || data.session_id;
        const session_name = data.data?.name || data.name;

        const applyFallback = () => {
          const newSibtfReport = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'sibtf_report',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionSibtfReport(
            sessionId,
            newSibtfReport,
          );
          try {
            const notifKey = sessionId
              ? `sibtf_report-${sessionId}`
              : `sibtf_report-${newSibtfReport.id}`;
            this.notifyWithCase(
              'info',
              'SIBTF Report generated',
              'A new SIBTF report has been generated.',
              notifKey,
              sessionId,
              session_name,
            );
          } catch {}
          if (sessionId) this.sibtfReportUpdateEmitter.next(sessionId);
        };

        if (!sessionId) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === sessionId) {
            this.sessionStore
              .setActiveSession({ id: sessionId } as any)
              .subscribe({
                next: () => {
                  try {
                    const notifKey = `sibtf_report-${sessionId}`;
                    this.notifyWithCase(
                      'info',
                      'SIBTF Report generated',
                      'A new SIBTF report has been generated.',
                      notifKey,
                      sessionId,
                      session_name,
                    );
                  } catch {}
                  this.sibtfReportUpdateEmitter.next(sessionId);
                },
                error: () => applyFallback(),
              });
          } else {
            // Not active session; still show notification.
            try {
              const notifKey = `sibtf_report-${sessionId}`;
              this.notifyWithCase(
                'info',
                'SIBTF Report generated',
                'A new SIBTF report has been generated.',
                notifKey,
                sessionId,
                session_name,
              );
            } catch {}
          }
        } catch {
          applyFallback();
        }
      });

      userChannel?.bind('pi_report', (data: any) => {
        const nowIso = new Date().toISOString();
        const pi_report_session_id = data.data?.session_id || data.session_id;
        const session_name = data.data?.name || data.name;

        const applyFallback = () => {
          const newPiReport = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'pi_report',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionPiReport(
            pi_report_session_id,
            newPiReport,
          );
          try {
            const notifKey = pi_report_session_id
              ? `pi_report-${pi_report_session_id}`
              : `pi_report-${newPiReport.id}`;
            this.notifyWithCase(
              'info',
              'PI Report generated',
              'A new PI report has been generated.',
              notifKey,
              pi_report_session_id,
              session_name,
            );
          } catch (err) {
            // Error showing notification handled silently
          }
          if (pi_report_session_id) {
            this.piReportUpdateEmitter.next(pi_report_session_id);
          }
        };

        if (!pi_report_session_id) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === pi_report_session_id) {
            this.sessionStore
              .setActiveSession({ id: pi_report_session_id } as any)
              .subscribe({
                next: () => {
                  const nameFromStore = session_name;
                  try {
                    const notifKey = pi_report_session_id
                      ? `pi_report-${pi_report_session_id}`
                      : `pi_report-${crypto.randomUUID()}`;
                    this.notifyWithCase(
                      'info',
                      'PI Report generated',
                      'A new PI report has been generated.',
                      notifKey,
                      pi_report_session_id,
                      nameFromStore || session_name,
                    );
                  } catch (err) {
                    // Silent
                  }
                  if (pi_report_session_id) {
                    this.piReportUpdateEmitter.next(pi_report_session_id);
                  }
                },
                error: () => {
                  applyFallback();
                },
              });
          } else {
            const nameFromStore = session_name;
            try {
              const notifKey = pi_report_session_id
                ? `pi_report-${pi_report_session_id}`
                : `pi_report-${crypto.randomUUID()}`;
              this.notifyWithCase(
                'info',
                'PI Report generated',
                'A new PI report has been generated.',
                notifKey,
                pi_report_session_id,
                nameFromStore || session_name,
              );
            } catch (err) {
              // Silent
            }
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('medical_legal_report', (data: any) => {
        const nowIso = new Date().toISOString();
        const sessionId = data.data?.session_id || data.session_id;
        const sessionName = data.data?.name || data.name;

        const applyFallback = () => {
          const newReport = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'medical_legal_report',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionMedicalLegalReport(
            sessionId,
            newReport,
          );
          try {
            const notifKey = sessionId
              ? `medical_legal_report-${sessionId}`
              : `medical_legal_report-${newReport.id}`;
            this.notifyWithCase(
              'info',
              'Medical-Legal Report generated',
              'A new Medical-Legal report has been generated.',
              notifKey,
              sessionId,
              sessionName,
            );
          } catch (err) {}
          if (sessionId) {
            this.medicalLegalReportUpdateEmitter.next(sessionId);
          }
        };

        if (!sessionId) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === sessionId) {
            this.sessionStore
              .setActiveSession({ id: sessionId } as any)
              .subscribe({
                next: () => {
                  try {
                    const notifKey = sessionId
                      ? `medical_legal_report-${sessionId}`
                      : `medical_legal_report-${crypto.randomUUID()}`;
                    this.notifyWithCase(
                      'info',
                      'Medical-Legal Report generated',
                      'A new Medical-Legal report has been generated.',
                      notifKey,
                      sessionId,
                      sessionName,
                    );
                  } catch (err) {}
                  this.medicalLegalReportUpdateEmitter.next(sessionId);
                },
                error: () => {
                  applyFallback();
                },
              });
          } else {
            try {
              const notifKey = sessionId
                ? `medical_legal_report-${sessionId}`
                : `medical_legal_report-${crypto.randomUUID()}`;
              this.notifyWithCase(
                'info',
                'Medical-Legal Report generated',
                'A new Medical-Legal report has been generated.',
                notifKey,
                sessionId,
                sessionName,
              );
            } catch (err) {}
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('ptp_report', (data: any) => {
        const nowIso = new Date().toISOString();
        const sessionId = data.data?.session_id || data.session_id;
        const sessionName = data.data?.name || data.name;

        const applyFallback = () => {
          const newReport = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'ptp_report',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          // Update active session only if it's currently open
          this.sessionStore.updateActiveSessionPtpReport(
            sessionId,
            newReport as any,
          );
          try {
            const notifKey = sessionId
              ? `ptp_report-${sessionId}`
              : `ptp_report-${newReport.id}`;
            this.notifyWithCase(
              'info',
              'PTP Report generated',
              'A new PTP report has been generated.',
              notifKey,
              sessionId,
              sessionName,
            );
          } catch (err) {}
          if (sessionId) {
            this.ptpReportUpdateEmitter.next(sessionId);
          }
        };

        if (!sessionId) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === sessionId) {
            this.sessionStore
              .setActiveSession({ id: sessionId } as any)
              .subscribe({
                next: () => {
                  try {
                    const notifKey = sessionId
                      ? `ptp_report-${sessionId}`
                      : `ptp_report-${crypto.randomUUID()}`;
                    this.notifyWithCase(
                      'info',
                      'PTP Report generated',
                      'A new PTP report has been generated.',
                      notifKey,
                      sessionId,
                      sessionName,
                    );
                  } catch (err) {}
                  this.ptpReportUpdateEmitter.next(sessionId);
                },
                error: () => {
                  applyFallback();
                },
              });
          } else {
            try {
              const notifKey = sessionId
                ? `ptp_report-${sessionId}`
                : `ptp_report-${crypto.randomUUID()}`;
              this.notifyWithCase(
                'info',
                'PTP Report generated',
                'A new PTP report has been generated.',
                notifKey,
                sessionId,
                sessionName,
              );
            } catch (err) {}
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('physical_examination_questions', (data: any) => {
        const physical_exam_session_id =
          data.data?.session_id || data.session_id;

        const applyFallback = () => {
          const nowIso = new Date().toISOString();
          const newPhysicalExamQuestions = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type:
              data.data?.output_type || 'physical_examination_questions',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionPhysicalExaminationQuestions(
            physical_exam_session_id,
            newPhysicalExamQuestions,
          );
          this.physicalExamUpdateEmitter.next(physical_exam_session_id);
        };

        if (!physical_exam_session_id) {
          applyFallback();
          return;
        }

        // Fetch session from API so UI shows DB-stored value for this session.
        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === physical_exam_session_id) {
            this.sessionStore
              .setActiveSession({ id: physical_exam_session_id } as any)
              .subscribe({
                next: () => {
                  this.physicalExamUpdateEmitter.next(physical_exam_session_id);
                },
                error: () => {
                  applyFallback();
                },
              });
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('review_of_records', (data: any) => {
        const nowIso = new Date().toISOString();
        const review_of_records_session_id =
          data.data?.session_id || data.session_id;
        const session_name = data.data?.name || data.name;

        const applyFallback = () => {
          const newReviewOfRecords = {
            id: crypto.randomUUID(),
            value: data.data?.value || data.value,
            output_type: data.data?.output_type || 'review_of_records',
            task_id: data.data?.task_id,
            createdAt: data.data?.createdAt || nowIso,
            updatedAt: data.data?.updatedAt || nowIso,
          };
          this.sessionStore.updateActiveSessionReviewOfRecords(
            review_of_records_session_id,
            newReviewOfRecords,
          );

          try {
            const notifKey = review_of_records_session_id
              ? `review_of_records-${review_of_records_session_id}`
              : `review_of_records-${newReviewOfRecords.id}`;
            this.notifyWithCase(
              'info',
              'Records generated',
              'Review of Records has been generated.',
              notifKey,
              review_of_records_session_id,
              session_name,
            );
          } catch (err) {
            // Error showing notification handled silently
          }

          this.reviewOfRecordsUpdateEmitter.next(review_of_records_session_id);
        };

        if (!review_of_records_session_id) {
          applyFallback();
          return;
        }

        try {
          const currentActiveId = this.sessionStore.activeSession()?.id;
          if (currentActiveId === review_of_records_session_id) {
            this.sessionStore
              .setActiveSession({ id: review_of_records_session_id } as any)
              .subscribe({
                next: () => {
                  const nameFromStore = session_name;
                  try {
                    const notifKey = review_of_records_session_id
                      ? `review_of_records-${review_of_records_session_id}`
                      : `review_of_records-${crypto.randomUUID()}`;
                    this.notifyWithCase(
                      'info',
                      'Records generated',
                      'Review of Records has been generated.',
                      notifKey,
                      review_of_records_session_id,
                      nameFromStore || session_name,
                    );
                  } catch (err) {
                    // Silent
                  }
                  this.reviewOfRecordsUpdateEmitter.next(
                    review_of_records_session_id,
                  );
                },
                error: () => {
                  applyFallback();
                },
              });
          } else {
            const nameFromStore = session_name;
            try {
              const notifKey = review_of_records_session_id
                ? `review_of_records-${review_of_records_session_id}`
                : `review_of_records-${crypto.randomUUID()}`;
              this.notifyWithCase(
                'info',
                'Records generated',
                'Review of Records has been generated.',
                notifKey,
                review_of_records_session_id,
                nameFromStore || session_name,
              );
            } catch (err) {
              // Silent
            }
          }
        } catch (err) {
          applyFallback();
        }
      });

      userChannel?.bind('previsit_questionnaire', (data: any) => {
        const previsit_session_id = data.data?.session_id || data.session_id;
        if (previsit_session_id) {
          this.previsitQuestionnaireUpdateEmitter.next(previsit_session_id);
        }
      });

      // Move scribe handler to same level as other handlers
      userChannel?.bind('scribe', (data: any) => {
        console.log('[WS] scribe event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        const session_name = this.getEventName(data);
        try {
          this.refreshAiScribeJobFromEvent(session_id, job_id);
          this.notifyAiScribeEvent(
            'info',
            'Scribe generated',
            'A new scribe has been generated. Please select session to view report.',
            'scribe',
            job_id,
            session_id,
            session_name,
          );
        } catch (err) {
          // Error showing notification handled silently
          console.error('scribe websocket handler failed', err);
        }
      });

      // Silent counterpart to 'scribe', for a flow that ALSO produces a DOCX.
      //
      // The Temporal PR-1 path registers its DOCX and publishes
      // generated_document_changed, which already pops "PR-1 report ready".
      // Emitting 'scribe' as well stacked a second "A new scribe has been
      // generated" popup for the same report. The refresh still has to happen
      // — it is what repopulates contentIdByOutputType.PR1, the
      // sourceArtifactId the generated-document editor looks its DOCX up by,
      // and it keeps the list/unread state correct when the finishing session
      // is not the one on screen — so only the toast is dropped.
      //
      // Legacy PR flows keep 'scribe': there is no DOCX event behind them, so
      // that toast is their only notice.
      userChannel?.bind('scribe_refresh', (data: any) => {
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        try {
          this.refreshAiScribeJobFromEvent(
            session_id,
            job_id,
            undefined,
            false,
          );
        } catch (err) {
          console.error('scribe_refresh websocket handler failed', err);
        }
      });

      // Silent counterpart to 'scribe'. Same refresh, no notification: the
      // 'scribe' handler above pops "A new scribe has been generated", which
      // would be wrong for an RFA gate decision and would fire on every run.
      // 'sub_report_status' generalises 'rfa_status' across every sibling form
      // (RFA, Injection, Work Status). Both are bound: the backend emits only
      // the former now, but the latter must keep working for one release so a
      // message published by the previous build mid-deploy is not dropped.
      //
      // markUnviewed stays FALSE for both. These fire on every PR-1/PR-2 run —
      // a gate decision fires even when nothing is produced, and there are now
      // three forms firing instead of one — so re-flagging an already-read
      // scribe as new on each would be noise the user cannot clear.
      //
      // The one exception is a STANDALONE-form job (Injection / RFA / Work
      // Status generated on its own): there the form IS the job's report, so
      // its FAILED and NOT_REQUIRED outcomes are the user's only notice and
      // get a toast, with the reason when the user can fix it (see
      // notifyStandaloneFormOutcome). Its success already toasts through
      // generated_document_changed.
      for (const eventName of ['sub_report_status', 'rfa_status'] as const) {
        userChannel?.bind(eventName, (data: any) => {
          const session_id = this.getEventSessionId(data);
          const job_id = this.getEventJobId(data);
          try {
            this.refreshAiScribeJobFromEvent(
              session_id,
              job_id,
              undefined,
              false,
              (job) => this.notifyStandaloneFormOutcome(job, data, session_id),
            );
          } catch (err) {
            console.error(`${eventName} websocket handler failed`, err);
          }
        });
      }

      userChannel?.bind('whisper_retry_failed', (data: any) => {
        console.log('[WS] whisper_retry_failed event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        const session_name = this.getEventName(data);

        this.refreshAiScribeJobFromEvent(session_id, job_id);
        this.notifyAiScribeEvent(
          'error',
          'Audio processing failed',
          'Maximum audio retry attempts exceeded.',
          'whisper-retry-failed',
          job_id,
          session_id,
          session_name,
        );
      });

      userChannel?.bind('transcribe_end', (data: any) => {
        console.log('[WS] transcribe_end event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);

        this.refreshAiScribeJobFromEvent(session_id, job_id, () => {
          if (session_id) {
            this.transcribeEndEmitter.next({
              sessionId: session_id,
              jobId: job_id ?? '',
            });
          }
        });
      });

      // Drop the row from the store. Unlike the other scribe events there's
      // nothing to refetch — it's gone server-side.
      userChannel?.bind('ai_scribe_deleted', (data: any) => {
        console.log('[WS] ai_scribe_deleted event received', data);
        try {
          const payload = data?.data ?? data;
          const session_id = this.getEventSessionId(data);
          const aiScribeId = payload?.ai_scribe_id;
          if (!session_id || !aiScribeId) {
            return;
          }

          this.sessionStore.removeAiScribeJob(session_id, aiScribeId);
          this.aiScribeDeletedEmitter.next({
            sessionId: session_id,
            aiScribeId,
            jobName: payload?.job_name ?? null,
            deletedBy: payload?.deleted_by ?? null,
          });
        } catch (err) {
          console.error('ai_scribe_deleted websocket handler failed', err);
        }
      });

      userChannel?.bind('whisper-reprocess', (data: any) => {
        console.log('[WS] whisper-reprocess event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        const session_name = this.getEventName(data);

        this.refreshAiScribeJobFromEvent(session_id, job_id);
        this.notifyAiScribeEvent(
          'warning',
          'Audio processing failed - Retry Required',
          'Please retry from Transcription Tab.',
          'whisper-retry-reprocess',
          job_id,
          session_id,
          session_name,
        );
      });

      userChannel?.bind('scribe-reprocess', (data: any) => {
        console.log('[WS] scribe-reprocess event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        const session_name = this.getEventName(data);

        this.refreshAiScribeJobFromEvent(session_id, job_id);
        this.notifyAiScribeEvent(
          'info',
          'Report generation failed - Retry Required',
          'Please retry from Transcription Summary/PR Tab.',
          'scribe-retry-reprocess',
          job_id,
          session_id,
          session_name,
        );
      });
      userChannel?.bind('scribe_retry_failed', (data: any) => {
        console.log('[WS] scribe_retry_failed event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        const session_name = this.getEventName(data);

        this.refreshAiScribeJobFromEvent(session_id, job_id);
        this.notifyAiScribeEvent(
          'error',
          'Report generation failed',
          'Maximum Report generation retry attempts exceeded.',
          'scribe-retry-failed',
          job_id,
          session_id,
          session_name,
        );
      });

      userChannel?.bind('queue_consumption_retry_failed', (data: any) => {
        console.log('[WS] queue_consumption_retry_failed event received', data);
        const session_id = this.getEventSessionId(data);
        const job_id = this.getEventJobId(data);
        const session_name = this.getEventName(data);

        this.refreshAiScribeJobFromEvent(session_id, job_id);
        this.notifyAiScribeEvent(
          'error',
          'Submission failed',
          'Consumer service likely down.',
          'queue-consumption-retry-failed',
          job_id,
          session_id,
          session_name,
        );
      });

      // ── Voice report edit events (arrive via Pusher, type set as event name) ──
      userChannel?.bind('report_edit_completed', (data: any) => {
        const payload = data?.data ?? data ?? {};
        const editSessionId: string =
          payload?.session_id ?? payload?.sessionId ?? data?.session_id;
        const editedFields: string[] = payload?.editedFields ?? [];

        this.reportEditCompletedEmitter.next({
          sessionId: editSessionId,
          outputType: payload?.outputType ?? payload?.type,
          newOutputId: payload?.newOutputId ?? null,
          editedFields,
        });

        if (!editSessionId) return;

        // Re-fetch the full session exactly like qme_report event does —
        // this pulls the new Output row and updates the report viewer
        const currentActiveId = this.sessionStore.activeSession()?.id;
        if (currentActiveId === editSessionId) {
          this.sessionStore
            .setActiveSession({ id: editSessionId } as any)
            .subscribe({
              next: () => {
                this.qmeReportUpdateEmitter.next(editSessionId);
                try {
                  const fieldSummary = editedFields.length
                    ? `${editedFields.length} section${editedFields.length > 1 ? 's' : ''} updated.`
                    : 'No sections changed.';
                  this.notifyWithCase(
                    'success',
                    'Report updated',
                    `Voice edit applied — ${fieldSummary}`,
                    `report_edit_completed-${editSessionId}-${Date.now()}`,
                    editSessionId,
                    undefined,
                  );
                } catch {
                  /* silent */
                }
              },
              error: () => {
                // Fallback: just emit the update emitter so UI refreshes
                this.qmeReportUpdateEmitter.next(editSessionId);
              },
            });
        } else {
          this.qmeReportUpdateEmitter.next(editSessionId);
        }
      });

      userChannel?.bind('report_edit_failed', (data: any) => {
        const payload = data?.data ?? data ?? {};
        const editSessionId: string =
          payload?.session_id ?? payload?.sessionId ?? data?.session_id;

        this.reportEditFailedEmitter.next({
          sessionId: editSessionId,
          outputType: payload?.outputType ?? payload?.type,
        });

        try {
          this.notifyWithCase(
            'error',
            'Voice edit failed',
            'The voice edit could not be applied. Please try again.',
            `report_edit_failed-${editSessionId}-${Date.now()}`,
            editSessionId,
            undefined,
          );
        } catch {
          /* silent */
        }
      });

      userChannel?.bind('report_edit_started', (data: any) => {
        const payload = data?.data ?? data ?? {};
        this.reportEditStartedEmitter.next({
          sessionId:
            payload?.session_id ?? payload?.sessionId ?? data?.session_id,
          outputType: payload?.outputType ?? payload?.type,
          instruction: payload?.instruction,
        });
      });

      // ── Voice AI-SCRIBE edit events (PR-1 / PR-2 / PR-4 / RFA) ──────────
      //
      // Their own event names, so none of the three QME binds above ever sees
      // scribe traffic. The broker forwards unknown types as-is, so no API
      // change was needed to introduce them.
      userChannel?.bind('scribe_edit_started', (data: any) => {
        const payload = data?.data ?? data ?? {};
        this.scribeEditStartedEmitter.next({
          sessionId:
            payload?.session_id ?? payload?.sessionId ?? data?.session_id,
          aiScribeId: payload?.aiScribeId,
          outputType: payload?.outputType,
          instruction: payload?.instruction,
        });
      });

      userChannel?.bind('scribe_edit_completed', (data: any) => {
        const payload = data?.data ?? data ?? {};
        const editSessionId: string =
          payload?.session_id ?? payload?.sessionId ?? data?.session_id;
        const aiScribeId: string = payload?.aiScribeId;
        const editedFields: string[] = payload?.editedFields ?? [];

        this.scribeEditCompletedEmitter.next({
          sessionId: editSessionId,
          aiScribeId,
          outputType: payload?.outputType,
          newContentId: payload?.newOutputId ?? null,
          newVersion: payload?.newVersion ?? null,
          editedFields,
        });

        if (!editSessionId) return;

        // Re-fetch the scribe jobs (WITH AiScribeContents) and re-activate the
        // one this edit belongs to, so an open PR/RFA tab picks up the new
        // content version. markUnviewed=false: the physician is looking at this
        // scribe right now, so re-flagging it as unread would be noise.
        this.refreshAiScribeJobFromEvent(
          editSessionId,
          aiScribeId,
          () => {
            try {
              const fieldSummary = editedFields.length
                ? `${editedFields.length} section${editedFields.length > 1 ? 's' : ''} updated.`
                : 'No sections changed.';
              this.notifyWithCase(
                'success',
                'Report updated',
                `Voice edit applied — ${fieldSummary}`,
                `scribe_edit_completed-${editSessionId}-${Date.now()}`,
                editSessionId,
                undefined,
              );
            } catch {
              /* silent */
            }
          },
          false,
        );
      });

      userChannel?.bind('scribe_edit_failed', (data: any) => {
        const payload = data?.data ?? data ?? {};
        const editSessionId: string =
          payload?.session_id ?? payload?.sessionId ?? data?.session_id;

        this.scribeEditFailedEmitter.next({
          sessionId: editSessionId,
          aiScribeId: payload?.aiScribeId,
          outputType: payload?.outputType,
        });

        try {
          this.notifyWithCase(
            'error',
            'Voice edit failed',
            'The voice edit could not be applied. Please try again.',
            `scribe_edit_failed-${editSessionId}-${Date.now()}`,
            editSessionId,
            undefined,
          );
        } catch {
          /* silent */
        }
      });

      // Listen for notif_task events
      userChannel?.bind('notif_task', (data: any) => {
        const session_id = data.data?.session_id || data.session_id;
        const task_id = data.data?.task_id || data.task_id;
        if (session_id && task_id) {
          this.notifTaskUpdateEmitter.next({ session_id, task_id });
        }
      });

      // Lightweight status broadcast (RUNNING/COMPLETED/FAILED). On FAILED we
      // need to refresh the session so the frontend can stop a stuck spinner
      // and turn the Regenerate button red. Refetch only when the event is
      // for the active session to avoid unrelated network traffic.
      userChannel?.bind('report_generation_status', (data: any) => {
        try {
          const payload = data?.data ?? data ?? {};
          const session_id: string | undefined =
            payload.session_id || data?.session_id;
          const output_type: string | undefined = (payload.output_type ?? '')
            .toString()
            .trim()
            .toLowerCase();
          const status: string | undefined = (payload.status ?? '')
            .toString()
            .toUpperCase();

          if (!session_id || !output_type || !status) {
            return;
          }

          this.reportGenerationStatusEmitter.next({
            session_id,
            output_type,
            status,
          });

          // Persist the live status onto the active session so it survives a
          // report component being destroyed/recreated (e.g. switching tabs)
          // mid-generation. Without this, intermediate statuses live only in
          // transient component state and a remount re-enables the Regenerate
          // button. Terminal states are reconciled by the refetch below.
          if (this.sessionStore.activeSession()?.id === session_id) {
            this.sessionStore.updateActiveSessionReportGenerationStatus(
              session_id,
              output_type,
              status as any,
            );
          }

          // Temporal-run reports send ONLY this lightweight event — no content
          // event like the legacy `summary` one — so the "generated" toast and
          // the tab unread dot have to come from here.
          if (status === 'COMPLETED') {
            this.notifyReportCompletedFromStatus(session_id, output_type);
          }

          // Only refresh activeSession when the event is about the *current*
          // session AND we're transitioning to a terminal state. We avoid
          // re-fetching on RUNNING because that's already known locally
          // (we set RUNNING optimistically when triggering regenerate).
          if (status === 'FAILED' || status === 'COMPLETED') {
            const currentActiveId = this.sessionStore.activeSession()?.id;
            if (currentActiveId === session_id) {
              this.sessionStore
                .setActiveSession({ id: session_id } as any)
                .subscribe({
                  error: () => {
                    // best-effort; UI computed signals still drive button color
                  },
                });
            }
          }
        } catch {
          // never let a malformed payload break the websocket pipeline
        }
      });

      userChannel?.bind('ehr_push_status', (data: any) => {
        const event = this.parseEhrPushStatusEvent(data);
        if (!event) return;
        this.ehrPushStatusEmitter.next(event);
      });
    });

    this.pusher.connection.bind('error', (err: any) => {
      this.handlePusherReconnect();
    });

    this.pusher.connection.bind('disconnected', () => {
      this.stopHeartbeat();
      // Only retry if not intentionally disconnected
      if (this.pusher) {
        this.handlePusherReconnect();
      }
    });

    this.pusher.connection.bind('failed', () => {
      this.handlePusherReconnect();
    });
  }

  private handlePusherReconnect() {
    // Clear any existing Pusher reconnect timeout
    if (this.pusherReconnectTimeout) {
      clearTimeout(this.pusherReconnectTimeout);
    }

    if (this.pusherReconnectAttempts < this.maxReconnectAttempts) {
      this.pusherReconnectAttempts++;

      this.pusherReconnectTimeout = setTimeout(() => {
        // Disconnect existing Pusher instance if any
        if (this.pusher) {
          try {
            this.pusher.disconnect();
          } catch (err) {
            // Error disconnecting handled silently
          }
          this.pusher = null;
        }

        // Attempt to reconnect
        this.connectToPusher();

        // Check if connection succeeded after a delay
        setTimeout(() => {
          const pusherState = this.pusher?.connection.state;

          if (pusherState === 'connected') {
            // Connection successful, reset attempts
            this.pusherReconnectAttempts = 0;
          } else if (
            pusherState === 'disconnected' ||
            pusherState === 'failed' ||
            !this.pusher
          ) {
            // Connection failed, retry
            this.handlePusherReconnect();
          }
        }, 2000); // Wait 2 seconds to check connection status
      }, this.reconnectInterval);
    } else {
      this.pusherReconnectAttempts = 0; // Reset for next connection attempt
    }
  }

  private parseMmiInputsChangedEvent(data: any): MmiInputsChangedEvent | null {
    const payload = data?.data ?? data;
    if (!payload || typeof payload !== 'object') return null;

    const session_id = payload.session_id;
    const requestedAction = payload.requested_action;
    const mmiOutputId = payload.mmi_output_id;
    const changedEvidenceCategories = payload.changed_evidence_categories;

    if (
      typeof session_id !== 'string' ||
      !['qme', 'impairment'].includes(requestedAction) ||
      typeof mmiOutputId !== 'string' ||
      !Array.isArray(changedEvidenceCategories)
    ) {
      return null;
    }

    const reportId = readReportId(payload);
    return {
      session_id,
      requestedAction,
      mmiOutputId,
      changedEvidenceCategories,
      ...(reportId ? { reportId } : {}),
    };
  }

  private parseReportStartStoppedEvent(
    data: any,
  ): ReportStartStoppedEvent | null {
    const payload = data?.data ?? data;
    if (!payload || typeof payload !== 'object') return null;
    const { session_id, report_id, reason, message } = payload;
    if (
      typeof session_id !== 'string' ||
      typeof report_id !== 'string' ||
      typeof reason !== 'string'
    ) {
      return null;
    }
    return {
      session_id,
      report_id,
      reason,
      message: typeof message === 'string' ? message : '',
    };
  }

  private parseReportRunChangedEvent(data: any): ReportRunChangedEvent | null {
    const payload = data?.data ?? data;
    if (!payload || typeof payload !== 'object') return null;
    const { session_id, report_id, run_id, document_type, status, stage } =
      payload;
    if (
      typeof session_id !== 'string' ||
      typeof report_id !== 'string' ||
      typeof run_id !== 'string' ||
      typeof document_type !== 'string' ||
      typeof status !== 'string'
    ) {
      return null;
    }
    return {
      session_id,
      report_id,
      run_id,
      document_type,
      status: status as ReportRunChangedEvent['status'],
      stage: typeof stage === 'string' ? stage : null,
    };
  }

  private parseGeneratedDocumentChangedEvent(
    data: any,
  ): GeneratedDocumentChangedEvent | null {
    const payload = data?.data ?? data;
    if (!payload || typeof payload !== 'object') return null;

    const session_id = payload.session_id;
    const generated_document_id = payload.generated_document_id;
    const generated_document_type = payload.generated_document_type;
    const event_type = payload.event_type;

    const hasCommonFields =
      typeof session_id === 'string' &&
      typeof generated_document_id === 'string' &&
      // Derived from GENERATED_DOCUMENT_TYPES, not a hand-copied literal. The
      // duplicate list here is why a new document type would typecheck and then
      // be dropped at runtime with no log — the tab would simply never refresh
      // when its DOCX finished.
      isGeneratedDocumentType(generated_document_type);

    if (!hasCommonFields) return null;

    if (event_type === 'BASELINE_GENERATION_FAILED') {
      if (typeof payload.error_code !== 'string') return null;
      return {
        session_id,
        generated_document_id,
        generated_document_type,
        event_type,
        error_code: payload.error_code,
      };
    }

    if (
      typeof payload.active_version_id !== 'string' ||
      typeof payload.version_number !== 'number' ||
      !Number.isInteger(payload.version_number) ||
      !['BASELINE_REGISTERED', 'USER_VERSION_SAVED'].includes(event_type)
    ) {
      return null;
    }

    return {
      session_id,
      generated_document_id,
      generated_document_type,
      event_type,
      active_version_id: payload.active_version_id,
      version_number: payload.version_number,
    };
  }

  private parseEhrPushStatusEvent(data: any): EhrPushStatusEvent | null {
    const payload = data?.data ?? data;
    if (!payload || typeof payload !== 'object') return null;

    const session_id = payload.session_id;
    const generatedDocumentId = payload.generatedDocumentId;
    const generatedDocumentVersionId = payload.generatedDocumentVersionId;
    const ehrPushStatus = payload.ehrPushStatus;
    const workflowId = payload.workflowId;

    if (
      typeof session_id !== 'string' ||
      typeof generatedDocumentId !== 'string' ||
      typeof generatedDocumentVersionId !== 'string' ||
      (ehrPushStatus !== 'SUCCEEDED' && ehrPushStatus !== 'FAILED') ||
      typeof workflowId !== 'string'
    ) {
      return null;
    }

    return {
      session_id,
      generatedDocumentId,
      generatedDocumentVersionId,
      ehrPushStatus,
      workflowId,
    };
  }

  /**
   * The API keeps routing metadata at the top level and the fail-open
   * contract in `data`, unlike the clinical `mmi_status` event. This never
   * turns the operational event into an MMI output or status.
   */
  private parseMmiGateFailedOpenEvent(
    data: any,
  ): MmiGateFailedOpenEvent | null {
    const payload = data?.data ?? data ?? {};
    const sessionId = payload?.session_id ?? data?.session_id;
    const requestedAction = payload?.requestedAction;
    const warningMessage = payload?.warning?.message;

    if (
      !sessionId ||
      payload?.mmiGateFailedOpen !== true ||
      (requestedAction !== 'qme' && requestedAction !== 'impairment')
    ) {
      return null;
    }

    const reportId = readReportId(payload);
    return {
      sessionId,
      requestedAction,
      ...(typeof warningMessage === 'string' && warningMessage.trim().length > 0
        ? { warningMessage: warningMessage.trim() }
        : {}),
      ...(reportId ? { reportId } : {}),
    };
  }

  private getGeneratedDocumentFailureNotificationTitle(
    documentType: GeneratedDocumentChangedEvent['generated_document_type'],
  ): string {
    switch (documentType) {
      case 'QME_REPORT':
        return 'QME report failed';
      case 'SIBTF_REPORT':
        return 'SIBTF report failed';
      case 'PR2':
        return 'PR-2 report failed';
      case 'PI_REPORT':
        return 'PI report failed';
      case 'RFA':
        return 'RFA Generation Failed';
      case 'PR1':
        return 'PR-1 report failed';
      case 'PR4_REPORT':
        return 'PR-4 report failed';
      case 'PTP_REPORT':
        return 'PTP report failed';
      default:
        return 'Report generation failed';
    }
  }

  private getGeneratedDocumentNotificationTitle(
    documentType: GeneratedDocumentChangedEvent['generated_document_type'],
  ): string {
    switch (documentType) {
      case 'QME_REPORT':
        return 'QME report ready';
      case 'SIBTF_REPORT':
        return 'SIBTF report ready';
      case 'PR2':
        return 'PR-2 report ready';
      case 'PI_REPORT':
        return 'PI report ready';
      case 'PTP_REPORT':
        return 'PTP report ready';
      case 'RFA':
        return 'RFA report Ready';
      case 'WORK_STATUS':
        return 'Work Status report Ready';
      case 'INJECTION':
        return 'Injection Procedure report Ready';
      case 'PR1':
        return 'PR-1 report ready';
      case 'PR4_REPORT':
        return 'PR-4 report ready';
      default:
        return 'Report ready';
    }
  }

  private startHeartbeat(socketId: string) {
    // Send heartbeat every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      const orgUuid = this.userStore.organization()?.id;
      const userUuid = this.userStore.organizationUser()?.id;

      if (orgUuid && userUuid) {
        this.httpService
          .post(
            this.heartbeatEndpoint,
            {},
            {
              headers: {
                'x-connection-id': socketId,
              },
            },
          )
          .subscribe({
            error: (error) => {
              // Heartbeat error handled silently
            },
          });
      }
    }, 30000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Re-fetch the session's AI scribe jobs and, if the active job still matches,
  // Re-fetch the session's AI scribe jobs so the store (and media library) picks
  // up fresh recording refs. `setActiveJob` controls whether the open panel's
  // active job is also replaced: `scribe` sets it (generated output changed);
  // `recording_converted` does NOT — only the recording asset changed, and
  // re-activating the job mid-view would confuse the user (playback isn't
  // offered until the scribe fully completes anyway).
  private refreshActiveAiScribeJob(
    sessionId: string | undefined,
    setActiveJob = true,
  ): void {
    if (!sessionId) {
      return;
    }
    // Fetch WITH the full include set so the refresh replaces jobs with complete
    // data.
    this.sessionStore
      .fetchActiveSessionAiScribeJobs(
        sessionId,
        'AiScribeContents,FileAsset,ai_scribe_logs,ai_scribe_retry_logs',
      )
      ?.subscribe((aiScribeJobsSource: SourceAiScribeJob[]) => {
        if (!setActiveJob) {
          return;
        }
        if (aiScribeJobsSource && aiScribeJobsSource.length > 0) {
          const firstJob = aiScribeJobsSource[0];
          const currentActiveJob =
            this.sessionStore.activeSession()?.activeAiScribeJob;

          if (currentActiveJob?.aiScribeId === firstJob.aiScribeId) {
            const aiScribeJob: AiScribeJob = mapSourceToAiScribeJob(firstJob);

            this.sessionStore.setActiveSessionActiveAiScribe(
              sessionId,
              aiScribeJob,
            );
          }
        }
      });
  }

  private handleReconnect() {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      this.reconnectTimeout = setTimeout(() => {
        this.connect(false);
      }, this.reconnectInterval);
    } else {
      this.resetConnection();
    }
  }

  private resetConnection() {
    this.reconnectAttempts = 0;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }
    this.stopPing();
    if (this.connection) {
      this.connection.close();
      this.connection = undefined;
    }
    this._connectionState.set(ConnectionState.DISCONNECTED);
  }

  disconnect() {
    // Clear visibility reconnect timeout
    if (this.visibilityReconnectTimeout) {
      clearTimeout(this.visibilityReconnectTimeout);
      this.visibilityReconnectTimeout = undefined;
    }
    this.visibilityReconnectAttempts = 0;

    // Clear Pusher reconnect timeout
    if (this.pusherReconnectTimeout) {
      clearTimeout(this.pusherReconnectTimeout);
      this.pusherReconnectTimeout = undefined;
    }
    this.pusherReconnectAttempts = 0;

    if (this.visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
      this.visibilityChangeHandler = undefined;
    }

    if (this.pusher && this.socketId) {
      try {
        this.sendDisconnectToEndpoint();
      } catch (err) {
        // Error sending disconnect handled silently
      }
      this.stopHeartbeat();

      try {
        if (
          this.pusher &&
          typeof (this.pusher as any).disconnect === 'function'
        ) {
          (this.pusher as any).disconnect();
        }
      } catch (err) {
        // Error disconnecting pusher handled silently
      }

      this.pusher = null;
    }
    this.resetConnection();
    this.stopHeartbeat();
  }

  private sendDisconnectToEndpoint(isUnload = true) {
    const token = localStorage.getItem('access_token');
    const organizationUser = this.userStore.organizationUser();
    const organization = this.userStore.organization();
    const socketId = this.socketId;
    if (!token) {
      this.pusher?.disconnect();
      this.pusher = null;
      this.socketId = null;
      return;
    }

    const payload = {
      event: {
        channel: {
          connectionId: socketId,
        },
      },
      organization_uuid: organization?.id,
      user_uuid: organizationUser?.id,
    };

    const url = this.disconnectEndpoint;

    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      keepalive: isUnload,
    };

    if (isUnload) {
      fetch(url, options).catch((err) => {
        // Disconnect error handled silently
      });
    } else {
      this.http
        .post(url, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .subscribe({
          next: (response: any) => {
            // Disconnect response handled silently
          },
          error: (err: any) => {
            // Disconnect error handled silently
          },
        });
    }
  }

  // Add method to check if connected
  isConnected(): boolean {
    return this.connectionState() === ConnectionState.CONNECTED;
  }

  // Add method to check if can send messages
  canSendMessages(): boolean {
    return this.isConnected() && !!this.connectionId;
  }

  listenMessage<T>(actionType: string) {
    return new Promise<T>((resolve) => {
      this.messageEmitter
        .pipe(
          filter((message) => message.action === actionType),
          map((message) => message.data),
        )
        .subscribe((message) => {
          resolve(message);
        });
    });
  }

  listenMessages(actionType: string, callback: Function) {
    return this.messageEmitter
      .pipe(
        filter((message) => message.action === actionType),
        map((message) => message.data),
      )
      .subscribe((message) => {
        callback(message);
      });
  }

  send(action: string, prompt?: any) {
    if (!this.isConnected()) {
      throw new Error('Cannot send message: websocket not connected');
    }
    this.connection!.send(
      JSON.stringify({
        action: action,
        prompt: prompt,
      }),
    );
  }

  sendQueryMessage(
    sessionId: string,
    chat_session_id: string,
    sender: 'user',
    message: string,
  ) {
    return this.httpService.post(
      `${environment.api_host}sessions/${sessionId}/chat-sessions/${chat_session_id}/chat-messages`,
      {
        message,
        sender,
        connection_id: this.connectionId,
      },
    );
  }

  //   input case_session_id
  startSession(session_id: string) {
    return this.httpService.post<any>(
      `${environment.api_host}sessions/${session_id}/chat-sessions`,
      {},
    );
  }

  startPing() {
    this.pingInterval = setInterval(() => {
      this.sendPing();
    }, 30000);
  }

  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  }

  sendPing() {
    if (this.connection) {
      this.connection.send(
        JSON.stringify({
          action: 'ping',
        }),
      );
    }
  }

  sendPing_id() {
    if (this.connection && this.connectionId) {
      this.connection.send(
        JSON.stringify({
          action: 'ping',
          connection_id: this.connectionId,
        }),
      );
    }
  }
}

/** The report an MMI event's check was reserved for, in either key style. */
function readReportId(payload: any): string | undefined {
  const reportId = payload?.reportId ?? payload?.report_id;
  return typeof reportId === 'string' && reportId ? reportId : undefined;
}
